import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { X, Upload, AlertCircle, CheckCircle, Download } from "lucide-react";
import apiClient from "@/api/apiClient";
import { ENDPOINTS } from "@/api/endpoints";
import { showError, showWarning, showSuccess } from "@/utils/toastHelpers";
import { transformErrorsToExcelRows, getErrorReportColumnWidths } from "@/utils/medicineHelpers";

export default function MedicineBulkUpload({ onClose }) {
  const queryClient = useQueryClient();

  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [skippedRows, setSkippedRows] = useState([]);
  const [uploadResult, setUploadResult] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);

  // Note: Duplicate checking is done entirely on the backend for accuracy
  // Frontend no longer pre-filters or pre-checks duplicates

  const uploadMutation = useMutation({
    mutationFn: ({ medicines, updateExisting }) =>
      apiClient.post(ENDPOINTS.MEDICINES.BULK_UPLOAD, { medicines, updateExisting }),
    onSuccess: (response) => {
      setUploadResult(response);
      queryClient.invalidateQueries(["medicines"]);
    },
    onError: (error) => {
      console.error("Failed to upload medicines:", error);
      showError(
        "Failed to upload medicines",
        error.response?.data?.error || "Please try again"
      );
    },
  });

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setParsedData([]);
      setSkippedRows([]);
      setParseError(null);
      setUploadResult(null);
      parseFile(selectedFile);
    }
  };

  const parseFile = (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();

    setIsParsing(true);
    setParseError(null);

    if (fileExtension === "csv") {
      parseCSV(file);
    } else if (fileExtension === "xlsx" || fileExtension === "xls") {
      parseExcel(file);
    } else {
      setIsParsing(false);
      setParseError(
        "Unsupported file format. Please upload a CSV or Excel file."
      );
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n");
        const headers = lines[0].split(",").map((h) => h.trim());

        // Create a normalized header map (lowercase, no spaces)
        const headerMap = {};
        headers.forEach((header, index) => {
          const normalized = header.toLowerCase().replace(/\s+/g, "");
          headerMap[normalized] = index;
        });

        // Helper to get value by normalized column name
        const getValue = (values, ...columnNames) => {
          for (const colName of columnNames) {
            const normalized = colName.toLowerCase().replace(/\s+/g, "");
            const index = headerMap[normalized];
            if (index !== undefined && values[index]) {
              return values[index].trim();
            }
          }
          return "";
        };

        const data = [];
        const skipped = [];
        for (let i = 1; i < lines.length; i++) {
          if (lines[i].trim() === "") continue;

          const values = lines[i].split(",").map((v) => v.trim());

          // Map to standardized field names
          const row = {
            company: getValue(values, "company"),
            product: getValue(values, "product"),
            strength: getValue(values, "strength"),
            dosageForm: getValue(values, "dosageForm", "dosage form", "form"),
            packSize: getValue(values, "packSize", "pack size"),
            sellingPrice: getValue(values, "sellingPrice", "selling price", "price"),
            schedule: getValue(values, "schedule"),
            genericName: getValue(values, "genericName", "generic name"),
            nappiCode: getValue(values, "nappiCode", "nappi code"),
            category: getValue(values, "category"),
            notes: getValue(values, "notes"),
          };

          // Collect missing required fields
          const requiredFields = ["company", "product", "strength", "dosageForm", "packSize", "sellingPrice"];
          const missingFields = requiredFields.filter((field) => !row[field]);

          // Track original row number (i + 1 because i starts at 1 for data rows, so row 2 in Excel)
          const originalRow = i + 1;

          if (missingFields.length === 0) {
            data.push({ ...row, _originalRow: originalRow });
          } else {
            skipped.push({
              row: originalRow,
              data: row,
              missingFields,
            });
          }
        }

        setParsedData(data);
        setSkippedRows(skipped);
        setIsParsing(false);

        if (data.length === 0) {
          setParseError(
            "No valid medicine records found in CSV. Please ensure all required fields are present."
          );
        }
      } catch (error) {
        console.error("Error parsing CSV:", error);
        setParseError(
          "Failed to parse CSV file. Please check the file format."
        );
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read file. Please try again.");
      setIsParsing(false);
    };
    reader.readAsText(file);
  };

  const parseExcel = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Using a simple approach - load XLSX dynamically
        import("https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs")
          .then((XLSX) => {
            try {
              const data = new Uint8Array(e.target.result);
              const workbook = XLSX.read(data, { type: "array" });

              // Get first sheet
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];

              // Convert to JSON
              const jsonData = XLSX.utils.sheet_to_json(worksheet);

              // Helper to get value from row with case-insensitive, space-insensitive matching
              const getRowValue = (row, ...fieldNames) => {
                for (const fieldName of fieldNames) {
                  // Try exact match first
                  if (row[fieldName] !== undefined && row[fieldName] !== null) {
                    return String(row[fieldName]).trim();
                  }

                  // Try normalized match (lowercase, no spaces)
                  const normalized = fieldName.toLowerCase().replace(/\s+/g, "");
                  for (const key in row) {
                    const normalizedKey = key.toLowerCase().replace(/\s+/g, "");
                    if (normalizedKey === normalized && row[key] !== undefined && row[key] !== null) {
                      return String(row[key]).trim();
                    }
                  }
                }
                return "";
              };

              // Process rows and track skipped ones
              const validData = [];
              const skipped = [];
              const requiredFields = ["company", "product", "strength", "dosageForm", "packSize", "sellingPrice"];

              jsonData.forEach((row, index) => {
                const mappedRow = {
                  company: getRowValue(row, "COMPANY", "company"),
                  product: getRowValue(row, "PRODUCT", "product"),
                  strength: getRowValue(row, "STRENGTH", "strength"),
                  dosageForm: getRowValue(row, "DOSAGE FORM", "dosageForm", "dosage form", "form"),
                  packSize: getRowValue(row, "PACK SIZE", "packSize", "pack size"),
                  sellingPrice: getRowValue(row, "SELLING PRICE", "sellingPrice", "selling price", "price"),
                  schedule: getRowValue(row, "SCHEDULE", "schedule"),
                  genericName: getRowValue(row, "GENERIC NAME", "genericName", "generic name"),
                  nappiCode: getRowValue(row, "NAPPI CODE", "nappiCode", "nappi code"),
                  category: getRowValue(row, "CATEGORY", "category"),
                  notes: getRowValue(row, "NOTES", "notes"),
                };

                const missingFields = requiredFields.filter((field) => !mappedRow[field]);

                // Track original row number (index + 2: +1 for 0-based index, +1 for header row)
                const originalRow = index + 2;

                if (missingFields.length === 0) {
                  validData.push({ ...mappedRow, _originalRow: originalRow });
                } else {
                  skipped.push({
                    row: originalRow,
                    data: mappedRow,
                    missingFields,
                  });
                }
              });

              setParsedData(validData);
              setSkippedRows(skipped);
              setIsParsing(false);

              if (validData.length === 0) {
                setParseError(
                  "No valid medicine records found in Excel file. Please ensure all required fields are present."
                );
              }
            } catch (error) {
              console.error("Error processing Excel data:", error);
              setParseError(
                "Failed to process Excel file. Please ensure it is a valid Excel file."
              );
              setIsParsing(false);
            }
          })
          .catch((error) => {
            console.error("Error loading Excel parser:", error);
            setParseError(
              "Failed to load Excel parser. Please try again or use CSV format."
            );
            setIsParsing(false);
          });
      } catch (error) {
        console.error("Error parsing Excel:", error);
        setParseError(
          "Failed to parse Excel file. Please ensure it is a valid Excel file."
        );
        setIsParsing(false);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read file. Please try again.");
      setIsParsing(false);
    };
    reader.readAsArrayBuffer(file);
  };

  // Duplicate checking removed from frontend - backend handles it accurately

  const handleUpload = () => {
    if (parsedData.length === 0) {
      showWarning("No valid data to upload");
      return;
    }

    // Send all medicines to backend - backend will handle duplicate checking accurately
    uploadMutation.mutate({ medicines: parsedData, updateExisting });
  };

  const downloadErrorReport = async () => {
    if (!uploadResult || !uploadResult.errors || uploadResult.errors.length === 0) {
      showWarning("No errors to download");
      return;
    }

    try {
      // Dynamically import xlsx
      const XLSX = await import("https://cdn.sheetjs.com/xlsx-0.20.1/package/xlsx.mjs");

      // Transform errors to Excel rows using utility function
      const errorRows = transformErrorsToExcelRows(uploadResult.errors);

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(errorRows);

      // Set column widths using utility function
      worksheet['!cols'] = getErrorReportColumnWidths();

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Errors");

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `medicine-upload-errors-${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      showSuccess("Error report downloaded successfully");
    } catch (error) {
      console.error("Error generating report:", error);
      showError("Failed to generate error report", "Please try again");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-slate-900">
            Bulk Upload Medicines
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">
              File Format (CSV or Excel)
            </h3>
            <p className="text-sm text-blue-800 mb-2">
              Your file should have the following columns (first 6 are
              required):
            </p>
            <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
              <li>
                <strong>COMPANY</strong>: Manufacturer name (e.g., Adcock)
              </li>
              <li>
                <strong>PRODUCT</strong>: Product name (e.g., Amfexa)
              </li>
              <li>
                <strong>STRENGTH</strong>: Dosage strength (e.g., 5mg)
              </li>
              <li>
                <strong>DOSAGE FORM</strong>: Form (e.g., Tablets, Capsules)
              </li>
              <li>
                <strong>PACK SIZE</strong>: Pack size (e.g., 30's)
              </li>
              <li>
                <strong>SELLING PRICE</strong>: Price per pack (e.g., 120.50)
              </li>
              <li>SCHEDULE: Medicine schedule (e.g., 6, S4) - optional</li>
              <li>GENERIC NAME: Generic name (optional)</li>
              <li>NAPPI CODE: NAPPI code (optional)</li>
              <li>
                CATEGORY: medication, supplement, hormone, or vitamin (optional)
              </li>
              <li>NOTES: Additional notes (optional)</li>
            </ul>
            <p className="text-xs text-blue-700 mt-3">
              <strong>Tip:</strong> Download the CSV template, open it in Excel,
              fill in your data, and save as either CSV or Excel (.xlsx) format.
            </p>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Select CSV or Excel File
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                disabled={isParsing}
                className="flex-1 text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
              />
            </div>
            {file && (
              <p className="mt-2 text-sm text-slate-600">
                Selected: {file.name}
              </p>
            )}
            {isParsing && (
              <p className="mt-2 text-sm text-blue-600 flex items-center gap-2">
                <Upload className="w-4 h-4 animate-spin" />
                Parsing file...
              </p>
            )}
          </div>

          {/* Parse Error */}
          {parseError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 mb-1">
                    Parsing Error
                  </h4>
                  <p className="text-sm text-red-800">{parseError}</p>
                </div>
              </div>
            </div>
          )}

          {/* Update Mode Toggle */}
          {parsedData.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2">
                    Upload Mode
                  </h4>
                  <p className="text-sm text-blue-800 mb-3">
                    Choose how to handle medicines that already exist in your catalog.
                  </p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={updateExisting}
                      onChange={(e) => setUpdateExisting(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-blue-900">
                      Update existing medicines (overwrite price, schedule, and other fields)
                    </span>
                  </label>
                  {!updateExisting && (
                    <p className="text-xs text-blue-700 mt-2 ml-6">
                      Duplicate medicines will be skipped. Only new medicines will be added.
                    </p>
                  )}
                  {updateExisting && (
                    <p className="text-xs text-blue-700 mt-2 ml-6">
                      Duplicate medicines will be updated with new data from the file. New medicines will be created.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Skipped Rows Summary */}
          {skippedRows.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-900 mb-1">
                    {skippedRows.length} row{skippedRows.length !== 1 ? 's' : ''} skipped due to missing columns
                  </h4>
                  <p className="text-sm text-amber-800 mb-2">
                    The following rows were skipped because they are missing required fields:
                  </p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {skippedRows.slice(0, 10).map((skipped, index) => (
                      <div
                        key={index}
                        className="text-xs bg-white/50 p-2 rounded border border-amber-200"
                      >
                        <span className="font-semibold">Row {skipped.row}:</span>{" "}
                        Missing {skipped.missingFields.join(", ")}
                        {skipped.data.product && ` (${skipped.data.product})`}
                      </div>
                    ))}
                  </div>
                  {skippedRows.length > 10 && (
                    <p className="text-xs text-amber-700 mt-2">
                      ... and {skippedRows.length - 10} more rows
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          {parsedData.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-900 mb-2">
                Preview ({parsedData.length} medicines)
              </h3>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                          Company
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                          Product
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                          Strength
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                          Form
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                          Pack Size
                        </th>
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">
                          Schedule
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {parsedData.slice(0, 10).map((row, index) => (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="px-3 py-2">{row.company}</td>
                          <td className="px-3 py-2">{row.product}</td>
                          <td className="px-3 py-2">{row.strength}</td>
                          <td className="px-3 py-2">{row.dosageForm}</td>
                          <td className="px-3 py-2">{row.packSize}</td>
                          <td className="px-3 py-2">{row.schedule}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {parsedData.length > 10 && (
                  <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 text-center border-t border-slate-200">
                    Showing first 10 of {parsedData.length} medicines
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <div
              className={`rounded-lg p-4 ${
                uploadResult.failed === 0
                  ? "bg-green-50 border border-green-200"
                  : "bg-yellow-50 border border-yellow-200"
              }`}
            >
              <div className="flex items-start gap-3">
                {uploadResult.failed === 0 ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4
                    className={`font-semibold mb-1 ${
                      uploadResult.failed === 0
                        ? "text-green-900"
                        : "text-yellow-900"
                    }`}
                  >
                    Upload Complete
                  </h4>
                  <p
                    className={`text-sm ${
                      uploadResult.failed === 0
                        ? "text-green-800"
                        : "text-yellow-800"
                    }`}
                  >
                    Successfully created: {uploadResult.success} medicines
                    {uploadResult.updated > 0 &&
                      ` | Updated: ${uploadResult.updated}`}
                    {uploadResult.skipped > 0 &&
                      ` | Skipped duplicates: ${uploadResult.skipped}`}
                    {uploadResult.failed > 0 &&
                      ` | Failed: ${uploadResult.failed}`}
                  </p>

                  {uploadResult.errors && uploadResult.errors.length > 0 && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-yellow-900">
                          {uploadResult.errors.filter(e => e.type !== 'duplicate').length > 0 &&
                            uploadResult.errors.filter(e => e.type === 'duplicate').length > 0
                            ? 'Errors & Duplicates:'
                            : uploadResult.errors.filter(e => e.type === 'duplicate').length > 0
                            ? 'Duplicates:'
                            : 'Errors:'}
                        </p>
                        <Button
                          onClick={downloadErrorReport}
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download Error Report
                        </Button>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-2">
                        {uploadResult.errors.slice(0, 5).map((error, index) => (
                          <div
                            key={index}
                            className={`text-xs bg-white/50 p-2 rounded border ${
                              error.type === 'duplicate'
                                ? 'border-blue-300 bg-blue-50/30'
                                : 'border-yellow-300'
                            }`}
                          >
                            <span className="font-semibold">
                              Row {error.row}:
                            </span>{" "}
                            {error.error}
                          </div>
                        ))}
                      </div>
                      {uploadResult.errors.length > 5 && (
                        <p className="text-xs text-yellow-700 mt-2">
                          ... and {uploadResult.errors.length - 5} more
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button type="button" onClick={onClose} variant="outline">
              {uploadResult && uploadResult.failed === 0 ? "Close" : "Cancel"}
            </Button>
            {!uploadResult && (
              <Button
                onClick={handleUpload}
                disabled={
                  uploadMutation.isPending ||
                  isParsing ||
                  parsedData.length === 0
                }
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                {uploadMutation.isPending ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : isParsing ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-spin" />
                    Parsing...
                  </>
                ) : updateExisting ? (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {parsedData.length} Medicines (Create + Update)
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload {parsedData.length} Medicines
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
